import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Customers.module.css';
import { 
  IconUsers, 
  IconCloud, 
  IconHistory, 
  IconQuestionMark, 
  IconChartBar,
  IconSnowflake,
  IconCheck,
  IconMapPin,
  IconChevronRight,
  IconCalendar,
  IconFileText
} from '@tabler/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Customers = () => {
  const navigate = useNavigate();

  const customerCards = [
    { icon: <IconChartBar size={24} stroke={1.5} />, label: 'Requests', route: '/requests' },
    { icon: <IconCloud size={24} stroke={1.5} />, label: 'Storm Strategy', route: '/storm-strategy' },
    { icon: <IconHistory size={24} stroke={1.5} />, label: 'Strategy History', route: '/storm-strategies' },
    { icon: <IconQuestionMark size={24} stroke={1.5} />, label: 'Survey Responses', route: '/storm-strategy-surveys' }
  ];

  // Quick actions data
  const quickActions = [
    { icon: <IconFileText size={18} />, label: 'View Regular Requests', action: () => navigate('/regular-requests') },
    { icon: <IconSnowflake size={18} />, label: 'View SnowEdge Requests', action: () => navigate('/snowedge-requests') },
    { icon: <IconCloud size={18} />, label: 'Create Bulk Storm Strategy', action: () => navigate('/create-storm-strategy') },
    { icon: <IconCheck size={18} />, label: 'Check Past Strategies', action: () => navigate('/past-strategies') }
  ];

  // Analytics data
  const analyticsData = {
    snowEdgeRequests: { count: 12, change: -5 },
    regularRequests: { count: 12, change: -5 },
    responseRate: { count: "13%", change: -5 },
    responseRateYear: { count: "45%", change: 10 }
  };

  // Chart data
  const chartData = [
    { name: 'Jan', snowEdge: 30, regular: 40 },
    { name: 'Feb', snowEdge: 45, regular: 30 },
    { name: 'Mar', snowEdge: 65, regular: 40 },
    { name: 'Apr', snowEdge: 40, regular: 30 },
    { name: 'May', snowEdge: 50, regular: 45 },
    { name: 'Jun', snowEdge: 35, regular: 60 },
    { name: 'Jul', snowEdge: 40, regular: 30 },
    { name: 'Aug', snowEdge: 50, regular: 35 },
    { name: 'Sep', snowEdge: 65, regular: 55 },
  ];

  // Latest requests data
  const latestRequests = [
    {
      id: 1,
      type: 'snowEdge',
      title: 'Submitted - SnowEdge Request from 310 Shipwill',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'submitted'
    },
    {
      id: 2,
      type: 'regular',
      title: 'Claimed - Request from 6800 Maritz Drive',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'claimed'
    },
    {
      id: 3,
      type: 'regular',
      title: 'Pending - Request from 6800 Maritz Drive',
      time: 'Feb 12, 2025 12:09 PM EST',
      status: 'pending'
    }
  ];

  // Active regions data
  const activeRegions = [
    {
      id: 1,
      location: 'Toronto',
      type: 'Strategy',
      time: 'Feb 12, 2023 12:09 PM EST'
    },
    {
      id: 2,
      location: 'Brampton',
      type: 'Survey',
      time: 'Feb 12, 2023 12:09 PM EST'
    },
    {
      id: 3,
      location: 'Toronto',
      type: 'Strategy',
      time: 'Feb 12, 2023 12:09 PM EST'
    },
    {
      id: 4,
      location: 'Brampton',
      type: 'Survey',
      time: 'Feb 12, 2023 12:09 PM EST'
    },
    {
      id: 5,
      location: 'Toronto',
      type: 'Strategy',
      time: 'Feb 12, 2023 12:09 PM EST'
    },
    {
      id: 6,
      location: 'Brampton',
      type: 'Survey',
      time: 'Feb 12, 2023 12:09 PM EST'
    }
  ];

  return (
    <div className={styles.customers}>
      <div className={styles.header}>
        <h2><IconUsers size={24} style={{ marginRight: '8px' }} /> Customers</h2>
        <p>Communicate storm strategies, organize client service requests and elicit client feedback. Request information for your team.</p>
      </div>

      {/* Customer cards */}
      <div className={styles.cardsContainer}>
        {customerCards.map((card, index) => (
          <button 
            key={index} 
            className={styles.operationCard}
            onClick={() => navigate(card.route)}
          >
            <span className={styles.cardLabel}>{card.label}</span>
            <div className={styles.cardIcon}>{card.icon}</div>
          </button>
        ))}
      </div>

      <div className={styles.lowerSection}>
        <div className={styles.leftColumn}>
          {/* Analytics Section */}
          <div className={styles.analyticsSection}>
            <h3>Analytics</h3>
            
            <div className={styles.analyticsCards}>
              {/* Requests Card */}
              <div className={styles.analyticsCard}>
                <div className={styles.analyticsCardHeader}>Requests</div>
                <div className={styles.analyticsCardMetrics}>
                  <div className={styles.metric}>
                    <div className={styles.metricChange} data-trend="up">
                      +{analyticsData.snowEdgeRequests.change}%
                    </div>
                    <div className={styles.metricValue}>
                      {analyticsData.snowEdgeRequests.count}
                    </div>
                    <div className={styles.metricLabel}>
                      Approved SnowEdge<br />requests this month
                    </div>
                  </div>
                  <div className={styles.metric}>
                    <div className={styles.metricChange} data-trend="down">
                      -{Math.abs(analyticsData.regularRequests.change)}%
                    </div>
                    <div className={styles.metricValue}>
                      {analyticsData.regularRequests.count}
                    </div>
                    <div className={styles.metricLabel}>
                      Approved Regular<br />requests this month
                    </div>
                  </div>
                </div>
              </div>

              {/* Survey Response Rate Card */}
              <div className={styles.analyticsCard}>
                <div className={styles.analyticsCardHeader}>Survey Response Rate</div>
                <div className={styles.analyticsCardMetrics}>
                  <div className={styles.metric}>
                    <div className={styles.metricChange} data-trend="up">
                      +{analyticsData.responseRate.change}%
                    </div>
                    <div className={styles.metricValue}>
                      {analyticsData.responseRate.count}
                    </div>
                    <div className={styles.metricLabel}>
                      Response Rate this<br />month
                    </div>
                  </div>
                  <div className={styles.metric}>
                    <div className={styles.metricChange} data-trend="down">
                      -{Math.abs(analyticsData.responseRateYear.change)}%
                    </div>
                    <div className={styles.metricValue}>
                      {analyticsData.responseRateYear.count}
                    </div>
                    <div className={styles.metricLabel}>
                      Response Rate this<br />year
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.chartBox}>
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#787878' }} />
                    <YAxis hide={true} />
                    <Tooltip />
                    <Line type="monotone" dataKey="snowEdge" stroke="#43A6DD" activeDot={{ r: 8 }} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="regular" stroke="#65C38E" activeDot={{ r: 8 }} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Latest Requests */}
          <div className={styles.latestRequests}>
            <h3>Latest Requests</h3>
            <div className={styles.tabsList}>
              <button className={`${styles.tabButton} ${styles.active}`}>All</button>
              <button className={styles.tabButton}>SnowEdge</button>
              <button className={styles.tabButton}>Regular</button>
            </div>
            
            <div className={styles.requestsList}>
              {latestRequests.map(request => (
                <div key={request.id} className={styles.requestItem}>
                  <div className={styles.requestDot} data-status={request.status}></div>
                  <div className={styles.requestContent}>
                    <div className={styles.requestTitle}>{request.title}</div>
                    <div className={styles.requestTime}>{request.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          {/* Quick Actions */}
          <div className={styles.actions}>
            <h3>Quick Actions</h3>
            <div className={styles.actionButtons}>
              {quickActions.map((action, index) => (
                <button key={index} className={styles.actionButton} onClick={action.action}>
                  <span className={styles.actionIcon}>{action.icon}</span>
                  <span className={styles.actionLabel}>{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Regions */}
          <div className={styles.activeRegions}>
            <h3>Active Regions</h3>
            <div className={styles.regionsList}>
              {activeRegions.map(region => (
                <div key={region.id} className={styles.regionItem}>
                  <div className={styles.regionLocation}>
                    {region.location}
                    <span className={styles.regionBadge} data-type={region.type.toLowerCase()}>
                      {region.type}
                    </span>
                  </div>
                  <div className={styles.regionTime}>{region.time}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
